import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import * as apiActions from '../data-layer/violationsReportApi';
import {
  type ReportColumnState,
  type SodViolationReportData,
} from '../data-layer/violationsReportTypes';

// Legacy export banner state removed (migrated to centralized banner queue)

export interface ReportColumnTypes {
  label: string;
  value: string;
  isChecked: boolean;
  isDisabled: boolean;
}

const initialState = {
  isLoading: false,
  reportModalTab: '',
  selectedReport: '',
  activeColumns: [] as ReportColumnTypes[],
  reportData: [] as SodViolationReportData[],
  isReportError: false,
  reportErrorReason: '',
  activeExports: [] as { reportType: string; progress: number }[],
  isPolling: false,
  exportProgress: {} as Record<string, number>,
  exportIdsByReportType: {} as Record<string, string>,
  exportStatusByReportType: {} as Record<string, string>,
  completedExportsCount: 0,
  lastStatusReportTypes: [] as string[],
  columnsByReportType: {} as Partial<
    Record<
      'SUMMARY' | 'DETAIL' | 'PERMISSIONS',
      Record<string, ReportColumnState[]>
    >
  >,
};

const violationsReportSlice = createSlice({
  name: 'aag/violationsReport',
  initialState,
  reducers: {
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setSelectedReport: (state, action: PayloadAction<string>) => {
      state.selectedReport = action.payload;
    },
    setReportModalTab: (state, action: PayloadAction<string>) => {
      state.reportModalTab = action.payload;
    },
    setReportColumns: (state, action: PayloadAction<ReportColumnTypes[]>) => {
      state.activeColumns = action.payload;
    },
    setReportError: (
      state,
      action: PayloadAction<{ state: boolean; reason?: string }>
    ) => {
      state.isReportError = action.payload.state;
      state.reportErrorReason = action.payload.reason ?? '';
    },
    setSodViolationsReportData: (
      state,
      action: PayloadAction<SodViolationReportData[]>
    ) => {
      state.reportData = action.payload;
    },
    // Removed legacy banner reducers
    addActiveExport: (
      state,
      action: PayloadAction<{ reportType: string; progress?: number } | string>
    ) => {
      const payload =
        typeof action.payload === 'string'
          ? { reportType: action.payload, progress: 0 }
          : {
              reportType: action.payload.reportType,
              progress: action.payload.progress ?? 0,
            };

      const existing = state.activeExports.find(
        (e) => e.reportType === payload.reportType
      );

      if (!existing) {
        state.activeExports.push(payload);
      } else {
        existing.progress = payload.progress;
      }
    },
    removeActiveExport: (state, action: PayloadAction<string>) => {
      state.activeExports = state.activeExports.filter(
        (e) => e.reportType !== action.payload
      );
    },
    clearActiveExports: (state) => {
      state.activeExports = [];
    },
    setExportProgress: (
      state,
      action: PayloadAction<{ reportType: string; progressPercentage: number }>
    ) => {
      const { reportType, progressPercentage } = action.payload;

      state.exportProgress[reportType] = progressPercentage;
    },
    setExportIdForReportType: (
      state,
      action: PayloadAction<{
        reportType: string;
        exportId?: string | null;
        status?: string;
      }>
    ) => {
      const { reportType, exportId, status } = action.payload;

      if (exportId) {
        state.exportIdsByReportType[reportType] = exportId;
      }

      if (status) {
        state.exportStatusByReportType[reportType] = status;
      }
    },
    setIsPolling: (state, action: PayloadAction<boolean>) => {
      state.isPolling = action.payload;
    },
    clearCompletedExports: (state) => {
      const newProgress: Record<string, number> = {};
      const newIds: Record<string, string> = {};
      const newStatus: Record<string, string> = {};

      Object.keys(state.exportProgress).forEach((reportType) => {
        const progress = state.exportProgress[reportType];

        if (typeof progress === 'number' && progress < 100) {
          newProgress[reportType] = progress;

          if (state.exportIdsByReportType[reportType]) {
            newIds[reportType] = state.exportIdsByReportType[reportType];
          }

          if (state.exportStatusByReportType[reportType]) {
            newStatus[reportType] = state.exportStatusByReportType[reportType];
          }
        }
      });

      state.exportProgress = newProgress;
      state.exportIdsByReportType = newIds;
      state.exportStatusByReportType = newStatus;
    },
    setCompletedExportsCount: (state, action: PayloadAction<number>) => {
      state.completedExportsCount = action.payload;
    },
    setLastStatusReportTypes: (state, action: PayloadAction<string[]>) => {
      state.lastStatusReportTypes = action.payload;
    },
    resetSelectedReport: (state) => {
      state.selectedReport = 'summary';
    },
    setColumnsForReportType: (
      state,
      action: PayloadAction<{
        reportType: 'SUMMARY' | 'DETAIL' | 'PERMISSIONS';
        columns: Record<string, ReportColumnState[]>;
      }>
    ) => {
      const { reportType, columns } = action.payload;

      state.columnsByReportType[reportType] = columns;
    },
  },
});

const { reducer: violationsReportReducer, actions: sliceActions } =
  violationsReportSlice;

export const violationsReportActions = {
  ...sliceActions,
  ...apiActions,
};
export default violationsReportReducer;
